import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Eye, Sparkles, CircleDot, Ear, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { useLanguage } from '../i18n/pageTranslations';

const CirugiaFacial = () => {
  const lang = useLanguage();

  const texts = {
    es: {
      eyebrow: 'Cirugía Facial Avanzada',
      title1: 'El tiempo, gestionado',
      title2: 'con precisión',
      description: 'El envejecimiento facial es un proceso progresivo que afecta piel, músculo y hueso de forma simultánea. La cirugía facial moderna aborda todos los planos de forma integrada, con resultados naturales y duraderos.',
      moreInfo: 'Más información',
      ctaEyebrow: 'Consulta Personalizada',
      ctaTitle: '¿Qué procedimiento es adecuado para usted?',
      ctaDesc: 'Cada rostro es único. En una consulta de valoración analizaremos sus objetivos y le propondremos el plan más adecuado.',
      ctaButton: 'Solicitar valoración',
    },
    en: {
      eyebrow: 'Advanced Facial Surgery',
      title1: 'Time, managed',
      title2: 'with precision',
      description: 'Facial aging is a progressive process that simultaneously affects skin, muscle and bone. Modern facial surgery addresses all layers in an integrated way, with natural and lasting results.',
      moreInfo: 'More information',
      ctaEyebrow: 'Personalized Consultation',
      ctaTitle: 'Which procedure is right for you?',
      ctaDesc: 'Each face is unique. In an assessment consultation we will analyze your goals and propose the most suitable plan.',
      ctaButton: 'Request assessment',
    },
    fr: {
      eyebrow: 'Chirurgie Faciale Avancée',
      title1: 'Le temps, géré',
      title2: 'avec précision',
      description: 'Le vieillissement facial est un processus progressif qui affecte simultanément la peau, le muscle et l\'os. La chirurgie faciale moderne aborde tous les plans de manière intégrée, avec des résultats naturels et durables.',
      moreInfo: 'Plus d\'informations',
      ctaEyebrow: 'Consultation Personnalisée',
      ctaTitle: 'Quelle procédure vous convient?',
      ctaDesc: 'Chaque visage est unique. Lors d\'une consultation d\'évaluation, nous analyserons vos objectifs et vous proposerons le plan le plus adapté.',
      ctaButton: 'Demander une évaluation',
    },
    pt: {
      eyebrow: 'Cirurgia Facial Avançada',
      title1: 'O tempo, gerenciado',
      title2: 'com precisão',
      description: 'O envelhecimento facial é um processo progressivo que afeta simultaneamente pele, músculo e osso. A cirurgia facial moderna aborda todos os planos de forma integrada, com resultados naturais e duradouros.',
      moreInfo: 'Mais informações',
      ctaEyebrow: 'Consulta Personalizada',
      ctaTitle: 'Qual procedimento é adequado para você?',
      ctaDesc: 'Cada rosto é único. Em uma consulta de avaliação analisaremos seus objetivos e proporemos o plano mais adequado.',
      ctaButton: 'Solicitar avaliação',
    },
  };

  const procedimientos = {
    es: [
      { titulo: 'Blefaroplastia', icono: Eye, descripcion: 'Cirugía de párpados superiores e inferiores. Elimina exceso cutáneo y bolsas adiposas. Recupera la mirada sin alterar la expresión.', url: '/blefaroplastia' },
      { titulo: 'Lifting Facial y Cervical', icono: Sparkles, descripcion: 'Reposicionamiento quirúrgico de tejidos faciales y cervicales. Resultado natural de larga duración.', url: '/lifting-facial' },
      { titulo: 'Rinoplastia', icono: CircleDot, descripcion: 'Corrección estética y/o funcional de la nariz. Planificación con morphing digital previo.', url: '/rinoplastia' },
      { titulo: 'Otoplastia', icono: Ear, descripcion: 'Corrección de orejas prominentes. Técnica mínimamente invasiva. Resultado permanente.', url: '/otoplastia' },
      { titulo: 'Ojeras y Bolsas Palpebrales', icono: EyeOff, descripcion: 'Tratamiento quirúrgico de la región periorbitaria. Blefaroplastia inferior con o sin reposición de grasa.', url: '/ojeras-bolsas' },
    ],
    en: [
      { titulo: 'Blepharoplasty', icono: Eye, descripcion: 'Upper and lower eyelid surgery. Removes excess skin and fat bags. Restores the look without altering expression.', url: '/blefaroplastia' },
      { titulo: 'Facelift', icono: Sparkles, descripcion: 'Surgical repositioning of facial and cervical tissues. Natural, long-lasting results.', url: '/lifting-facial' },
      { titulo: 'Rhinoplasty', icono: CircleDot, descripcion: 'Aesthetic and/or functional nose correction. Planning with prior digital morphing.', url: '/rinoplastia' },
      { titulo: 'Otoplasty', icono: Ear, descripcion: 'Correction of prominent ears. Minimally invasive technique. Permanent results.', url: '/otoplastia' },
      { titulo: 'Dark Circles and Eye Bags', icono: EyeOff, descripcion: 'Surgical treatment of the periorbital region. Lower blepharoplasty with or without fat repositioning.', url: '/ojeras-bolsas' },
    ],
    fr: [
      { titulo: 'Blépharoplastie', icono: Eye, descripcion: 'Chirurgie des paupières supérieures et inférieures. Élimine l\'excès cutané et les poches graisseuses.', url: '/blefaroplastia' },
      { titulo: 'Lifting Facial et Cervical', icono: Sparkles, descripcion: 'Repositionnement chirurgical des tissus faciaux et cervicaux. Résultat naturel et durable.', url: '/lifting-facial' },
      { titulo: 'Rhinoplastie', icono: CircleDot, descripcion: 'Correction esthétique et/ou fonctionnelle du nez. Planification avec morphing digital préalable.', url: '/rinoplastia' },
      { titulo: 'Otoplastie', icono: Ear, descripcion: 'Correction des oreilles décollées. Technique peu invasive. Résultat permanent.', url: '/otoplastia' },
      { titulo: 'Cernes et Poches Palpébrales', icono: EyeOff, descripcion: 'Traitement chirurgical de la région périorbitaire. Blépharoplastie inférieure avec ou sans repositionnement de graisse.', url: '/ojeras-bolsas' },
    ],
    pt: [
      { titulo: 'Blefaroplastia', icono: Eye, descripcion: 'Cirurgia de pálpebras superiores e inferiores. Remove excesso cutâneo e bolsas adiposas. Recupera o olhar sem alterar a expressão.', url: '/blefaroplastia' },
      { titulo: 'Lifting Facial e Cervical', icono: Sparkles, descripcion: 'Reposicionamento cirúrgico de tecidos faciais e cervicais. Resultado natural e duradouro.', url: '/lifting-facial' },
      { titulo: 'Rinoplastia', icono: CircleDot, descripcion: 'Correção estética e/ou funcional do nariz. Planejamento com morphing digital prévio.', url: '/rinoplastia' },
      { titulo: 'Otoplastia', icono: Ear, descripcion: 'Correção de orelhas proeminentes. Técnica minimamente invasiva. Resultado permanente.', url: '/otoplastia' },
      { titulo: 'Olheiras e Bolsas Palpebrais', icono: EyeOff, descripcion: 'Tratamento cirúrgico da região periorbital. Blefaroplastia inferior com ou sem reposição de gordura.', url: '/ojeras-bolsas' },
    ],
  };

  const t = texts[lang] || texts.es;
  const procs = procedimientos[lang] || procedimientos.es;

  return (
    <div className="min-h-screen bg-ivory">
      <Header />
      
      <section className="pt-12 pb-20 bg-cream">
        <div className="container mx-auto px-6 max-w-5xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow mb-6">{t.eyebrow}</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal mb-6">
              {t.title1}<br /><span className="text-gold">{t.title2}</span>
            </h1>
            <div className="w-16 h-px bg-gold mx-auto mb-8"></div>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">{t.description}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 bg-ivory">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="grid gap-8">
            {procs.map((proc, index) => {
              const Icon = proc.icono;
              return (
                <AnimatedSection key={index} animation="fadeUp" delay={index * 0.1}>
                  <Link to={proc.url} className="group card-luxury p-8 flex flex-col md:flex-row gap-8 items-start">
                    <div className="w-16 h-16 border border-gold flex items-center justify-center flex-shrink-0 group-hover:bg-gold transition-colors">
                      <Icon className="text-gold group-hover:text-charcoal transition-colors" size={24} />
                    </div>
                    <div className="flex-1">
                      <h2 className="font-serif text-2xl text-charcoal mb-3 group-hover:text-gold transition-colors">{proc.titulo}</h2>
                      <p className="text-text-secondary leading-relaxed mb-4">{proc.descripcion}</p>
                      <span className="inline-flex items-center gap-2 text-gold text-sm tracking-wider uppercase group-hover:gap-3 transition-all">
                        {t.moreInfo} <ArrowRight size={14} />
                      </span>
                    </div>
                  </Link>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6 max-w-3xl text-center">
          <AnimatedSection animation="fadeUp">
            <p className="eyebrow text-gold mb-6">{t.ctaEyebrow}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-ivory mb-6">{t.ctaTitle}</h2>
            <p className="text-gold-light/70 mb-10">{t.ctaDesc}</p>
            <Link to="/contacto" className="btn-gold inline-flex items-center gap-2 group">
              {t.ctaButton}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default CirugiaFacial;
